import { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Copy, ExternalLink, Tv, Check } from 'lucide-react';
import { toast } from 'react-toastify';

function PlaylistSettings({ user }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
  const DOMAIN = window.location.origin;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/signage/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (location) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/signage/config`, {
        [location]: config[location]
      });
      toast.success(`${location.charAt(0).toUpperCase() + location.slice(1)} settings saved`);
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (url, location) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(location);
    toast.success('URL copied to clipboard!');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const openPreview = (location) => {
    window.open(`${DOMAIN}/signage-display/${location}`, '_blank');
  };

  if (loading || !config) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const LocationSettings = ({ location, title, color }) => {
    const displayUrl = `${DOMAIN}/signage-display/${location}`;
    
    return (
      <div className="border rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-6">
          <div className={`p-3 rounded-lg ${color}`}>
            <Tv className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">Configure display settings</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Image Duration */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Default Image Duration
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="5"
                max="30"
                value={config[location].defaultImageDuration}
                onChange={(e) => setConfig({
                  ...config,
                  [location]: {
                    ...config[location],
                    defaultImageDuration: parseInt(e.target.value)
                  }
                })}
                className="flex-1"
              />
              <span className="text-sm font-medium text-gray-700 w-16">
                {config[location].defaultImageDuration}s
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              How long each image displays before moving to the next
            </p>
          </div>

          {/* Transition Effect */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Transition Effect
            </label>
            <select
              value={config[location].transitionEffect}
              onChange={(e) => setConfig({
                ...config,
                [location]: {
                  ...config[location],
                  transitionEffect: e.target.value
                }
              })}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="fade">Fade (Standard)</option>
              <option value="crossfade">Crossfade (Smooth Overlap)</option>
              <option value="fade-fast">Fade Fast (Quick)</option>
              <option value="fade-slow">Fade Slow (Gentle)</option>
              <option value="slide">Slide</option>
              <option value="slide-left">Slide Left</option>
              <option value="slide-right">Slide Right</option>
              <option value="zoom-fade">Zoom Fade</option>
              <option value="none">None (Instant)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Visual effect when transitioning between items - changes apply after TV refresh
            </p>
          </div>

          {/* Shuffle Media */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config[location].shuffleMedia}
                onChange={(e) => setConfig({
                  ...config,
                  [location]: {
                    ...config[location],
                    shuffleMedia: e.target.checked
                  }
                })}
                className="mr-3"
              />
              <div>
                <span className="text-sm font-medium">Shuffle Media</span>
                <p className="text-xs text-gray-500">
                  Randomize media playback order
                </p>
              </div>
            </label>
          </div>

          {/* Display URL */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Display URL
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={displayUrl}
                readOnly
                className="flex-1 border rounded-lg px-3 py-2 bg-gray-50 text-sm font-mono"
              />
              <button
                onClick={() => copyToClipboard(displayUrl, location)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  copiedUrl === location
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {copiedUrl === location ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <Copy className="h-5 w-5" />
                )}
              </button>
              <button
                onClick={() => openPreview(location)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <ExternalLink className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Use this URL in Fully Kiosk Browser on your Xiaomi Mi Box
            </p>
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t">
            <button
              onClick={() => handleSave(location)}
              disabled={saving}
              className={`w-full px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
              } text-white`}
            >
              <Save className="h-5 w-5" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Display Settings</h2>
        <p className="text-sm text-gray-600">
          Configure how media and announcements display on each TV
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LocationSettings 
          location="gym" 
          title="Gym Display" 
          color="bg-green-600"
        />
        <LocationSettings 
          location="salon" 
          title="Salon Display" 
          color="bg-pink-600"
        />
      </div>

      {/* Setup Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
          <Tv className="h-5 w-5" />
          Xiaomi Mi Box S Setup Instructions
        </h3>
        <ol className="space-y-2 text-sm text-blue-900">
          <li className="flex gap-2">
            <span className="font-bold">1.</span>
            <span>Install <strong>Fully Kiosk Browser</strong> on your Xiaomi Mi Box S</span>
          </li>
          <li className="flex gap-2">
            <span className="font-bold">2.</span>
            <span>Open Fully Kiosk and copy the Display URL from above</span>
          </li>
          <li className="flex gap-2">
            <span className="font-bold">3.</span>
            <span>In Fully Kiosk Settings → Set <strong>Start URL</strong> to the Display URL</span>
          </li>
          <li className="flex gap-2">
            <span className="font-bold">4.</span>
            <span>Enable: <strong>Start on Boot</strong>, <strong>Keep Screen On</strong>, <strong>Fullscreen Mode</strong></span>
          </li>
          <li className="flex gap-2">
            <span className="font-bold">5.</span>
            <span>Set Fully Kiosk as default Home launcher in Android Settings</span>
          </li>
          <li className="flex gap-2">
            <span className="font-bold">6.</span>
            <span>Restart the TV box - display should auto-load on boot!</span>
          </li>
        </ol>
      </div>
    </div>
  );
}

export default PlaylistSettings;